import { useEffect, useRef, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import {
  attachmentContentUrl,
  deleteAttachment,
  listAttachments,
  uploadAttachment,
  type AttachmentDocType,
  type AttachmentRow,
} from "@/shared/api/attachments";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentsPanel(props: {
  companySlug: string;
  docType: AttachmentDocType;
  docId: string;
  embedded?: boolean;
}) {
  const { companySlug, docType, docId, embedded } = props;
  const confirmUi = useConfirm();
  const [rows, setRows] = useState<AttachmentRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function reload() {
    const data = await listAttachments(companySlug, docType, docId);
    setRows(data.attachments);
  }

  useEffect(() => {
    let cancelled = false;
    reload().catch((err: unknown) => {
      if (!cancelled) {
        setError(err instanceof Error ? err.message : "Failed");
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug, docType, docId]);

  async function onPick(fileList: FileList | null) {
    const file = fileList?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadAttachment(companySlug, { docType, docId, file });
      if (inputRef.current) inputRef.current.value = "";
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  const body = (
    <>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {!embedded ? (
          <h2 className="m-0 text-xs font-semibold uppercase tracking-wider text-[var(--color-muted)]">
            Attachments
          </h2>
        ) : (
          <p className="m-0 text-sm text-[var(--color-muted)]">
            Files on this document
          </p>
        )}
        <div>
          <input
            ref={inputRef}
            type="file"
            className="sr-only"
            disabled={busy}
            onChange={(e) => void onPick(e.target.files)}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            Upload
          </Button>
        </div>
      </div>

      <ul className="mt-4 m-0 list-none space-y-3 p-0">
        {rows.map((r) => (
          <li
            key={r.id}
            className="flex flex-wrap items-baseline justify-between gap-2 text-sm"
          >
            <div className="min-w-0">
              <a
                href={attachmentContentUrl(companySlug, r.id)}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-[var(--color-foreground)] underline-offset-2 hover:underline"
              >
                {r.fileName}
              </a>
              <div className="text-[10px] text-[var(--color-muted)]">
                {formatBytes(r.byteSize)} · {r.uploaderLabel} ·{" "}
                {new Date(r.createdAt).toLocaleString("en-PH", {
                  timeZone: "Asia/Manila",
                })}
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                void (async () => {
                  const ok = await confirmUi({
                    title: `Remove “${r.fileName}”?`,
                    description:
                      "This deletes the file from the document. You cannot undo this.",
                    confirmLabel: "Remove",
                    destructive: true,
                  });
                  if (!ok) return;
                  setBusy(true);
                  setError(null);
                  try {
                    await deleteAttachment(companySlug, r.id);
                    await reload();
                  } catch (err: unknown) {
                    setError(err instanceof Error ? err.message : "Failed");
                  } finally {
                    setBusy(false);
                  }
                })();
              }}
            >
              Remove
            </Button>
          </li>
        ))}
        {rows.length === 0 && !error && (
          <li className="rounded-lg border border-dashed border-[var(--color-border)] px-3 py-5 text-center text-sm text-[var(--color-muted)]">
            No files yet
          </li>
        )}
      </ul>

      {error && (
        <Alert variant="destructive" className="mt-3">
          {error}
        </Alert>
      )}
    </>
  );

  if (embedded) return <div>{body}</div>;

  return (
    <section className="mt-10 border-t border-[var(--color-border)] pt-6">
      {body}
    </section>
  );
}
